import React, { useCallback, useEffect, useState } from "react";
import { logException } from "@wss/error-tracking";
import { User } from "~/common/Store/models/User";
import { RecAiPage, RecAiType } from "../RecAiCarousel/definition";
import fetchRecAiCarouselItems from "../RecAiCarousel/fetchRecAiCarouselItems";
import { CarouselItem } from "~/ProductDetails/Carousel/CarouselItem";
import { withUserAndChat } from "~/common/Store/connectUserAndChat";
import Carousel from "../Carousel";
import HomePageCarouselType from "./HomePageCarouselType";
import fetchProductRecommendationsCarouselItemsForUser from "../RecAiCarousel/fetchProductRecommendationsCarouselItemsForUser";
import { useHomePageData } from "../utilities";
import Advertisements from "../Advertisements";
import PlusAd from "../PlusAd";
import FeaturedCarousel from "../FeaturedCarousel";
import WaysToSave from "../WaysToSave";
import FeaturedCategories from "../FeaturedCategories";
import { defaultUserObject } from "~/common/Store/rootReducer";
import useIsMounted from "~/common/useIsMounted";
interface FeaturedItemsAndCarouselsProps {
  user: User;
}
const FeaturedItemsAndCarousels = ({
  user
}: FeaturedItemsAndCarouselsProps) => {
  const mounted = useIsMounted();
  const {
    bestSellingProductsCarousel
  } = useHomePageData();
  const [recommendedProductsForYou, setRecommendedProductsForYou] = useState<CarouselItem[]>([]);
  const [frequentlyBoughtTogether, setFrequentlyBoughtTogether] = useState<CarouselItem[]>([]);
  const [firstCarouselType, setFirstCarouselType] = useState<HomePageCarouselType>();
  const [secondCarouselType, setSecondCarouselType] = useState<HomePageCarouselType>();
  const bestSellingProducts = bestSellingProductsCarousel?.carouselMembers?.length ? bestSellingProductsCarousel?.carouselMembers : [];
  const getFrequentlyBoughtCarouselItems = useCallback(async () => {
    const result = await fetchRecAiCarouselItems(RecAiPage.Home, "Home", "https://www.webstaurantstore.com", [], RecAiType.FrequentlyBoughtTogether);
    if (result.isError) {
      logException(result.error);
    } else {
      setFrequentlyBoughtTogether(result.value);
    }
  }, []);
  useEffect(() => {
    getFrequentlyBoughtCarouselItems();
  }, [getFrequentlyBoughtCarouselItems]);
  useEffect(() => {
    const getRecommendationCarouselItems = async (email: string) => {
      const result = await fetchProductRecommendationsCarouselItemsForUser(RecAiPage.Home, email);
      if (result.isError) {
        logException(result.error);
      } else {
        setRecommendedProductsForYou(result.value);
      }
    };
    if (user.registered && user.emailAddress) {
      getRecommendationCarouselItems(user.emailAddress);
    }
  }, [user.registered, user.emailAddress]);
  useEffect(() => {
    let newFirstCarouselType: HomePageCarouselType | undefined;
    let newSecondCarouselType: HomePageCarouselType | undefined;
    const setCarouselItemsWithPriority = (type: HomePageCarouselType) => {
      if (!newFirstCarouselType) {
        newFirstCarouselType = type;
      } else if (!newSecondCarouselType) {
        newSecondCarouselType = type;
      }
    };
    if (recommendedProductsForYou?.length && user.registered) {
      newFirstCarouselType = HomePageCarouselType.RecommendedProductsForYou;
    }
    if (frequentlyBoughtTogether?.length) {
      setCarouselItemsWithPriority(HomePageCarouselType.FrequentlyBoughtTogether);
    }
    if (bestSellingProducts.length && !newSecondCarouselType) {
      newSecondCarouselType = HomePageCarouselType.BestSelling;
    }
    setFirstCarouselType(newFirstCarouselType);
    setSecondCarouselType(newSecondCarouselType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestSellingProducts.length,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  frequentlyBoughtTogether?.length,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  recommendedProductsForYou?.length, user.registered]);
  const renderRecommendedProductsForYou = (className = "") => <section id="recommend-for-you" data-gtm-component="carousel" className={`carousel-wrap ${className}`} data-placement-id="home-page" data-feature-name="Homepage Recommended Products" aria-label="Products Carousel" data-sentry-component="renderRecommendedProductsForYou" data-sentry-source-file="FeaturedItemsAndCarousels.tsx">
      <Carousel carouselItems={recommendedProductsForYou} id="recommend-for-you" gtmCarouselName="Recommended For You" data-sentry-element="Carousel" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
    </section>;
  const renderRecommendedProductsFromCart = (className = "") => <div id="frequently-bought-together-ctr" data-gtm-component="carousel" className={`carousel-wrap ${className}`} data-placement-id="frequently-bought-together-ctr" data-feature-name="Homepage Frequently Bought" data-sentry-component="renderRecommendedProductsFromCart" data-sentry-source-file="FeaturedItemsAndCarousels.tsx">
      <Carousel carouselItems={frequentlyBoughtTogether} id="frequently-bought-together-ctr" data-sentry-element="Carousel" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
    </div>;
  const renderBestSellingCarousel = <div id="best_selling" data-gtm-component="carousel" className="carousel-wrap pb-0" data-placement-id="best_selling" data-feature-name="Homepage Best Selling" gtm-carousel-name="Best Selling Products">
      <Carousel carouselItems={bestSellingProducts} id="best_selling" />
    </div>;

  /* Multiple carousels are being used for each type to prevent re-rendering of the inner components
    This can be consolidated once we have re-written common carousel code in React-friendly manner */

  const renderFirstCarouselByType = () => {
    switch (firstCarouselType) {
      case HomePageCarouselType.RecommendedProductsForYou:
        return renderRecommendedProductsForYou("mb-0 pt-6");
      case HomePageCarouselType.FrequentlyBoughtTogether:
        return renderRecommendedProductsFromCart("mb-0 pt-6");
      default:
        return <></>;
    }
  };
  const renderSecondCarouselByType = () => {
    switch (secondCarouselType) {
      case HomePageCarouselType.FrequentlyBoughtTogether:
        return renderRecommendedProductsFromCart;
      case HomePageCarouselType.BestSelling:
        return renderBestSellingCarousel;
      default:
        return <></>;
    }
  };
  return <>
      <div className="container">{renderFirstCarouselByType()}</div>
      <Advertisements data-sentry-element="Advertisements" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
      <div className="bg-white pt-6 xl:pt-10 pb-0">
        <PlusAd data-sentry-element="PlusAd" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
        <FeaturedCarousel data-sentry-element="FeaturedCarousel" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
        <div id="customerio-section1" />
        <div id="customerio-section2" />
        <WaysToSave mounted={mounted} user={defaultUserObject} data-sentry-element="WaysToSave" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
        <FeaturedCategories data-sentry-element="FeaturedCategories" data-sentry-source-file="FeaturedItemsAndCarousels.tsx" />
      </div>
      <div className="bg-white">
        <div className="container">
          <>
            <h2 className="text-center text-gray-800 text-2xl md:text-3xl mb-4">
              {secondCarouselType}
            </h2>
            {renderSecondCarouselByType()}
          </>
        </div>
      </div>
    </>;
};
export default withUserAndChat(FeaturedItemsAndCarousels);